import { useMutation } from "@tanstack/react-query";
import { updatePreferences } from "@/api/preferences";
import { usePreferencesManager } from "@/tanstackQuery/queries";
import { ThemeMode } from "@/types/ApiResponses/UserPreferences";
import { getThemeSettingsForMode } from "@/config/themeRules";

interface Variables {
  /** false = only record the marker, keep the current theme (legacy browsers
   *  that already applied the default from their local flag). */
  switchToDark: boolean;
}

// Zero's one-time dark default, recorded on the user's preferences so it can
// never fire twice for the same user — whatever browser they show up in.
export const useApplyZeroDarkDefault = () => {
  const prefsManager = usePreferencesManager();

  return useMutation({
    mutationFn: ({ switchToDark }: Variables) => {
      const dark = getThemeSettingsForMode(ThemeMode.Dark);
      return updatePreferences({
        applyZeroDarkDefault: true,
        ...(switchToDark
          ? { themeMode: dark.mode, themeColor: dark.color }
          : {}),
      });
    },

    onMutate: ({ switchToDark }) => {
      const previous = prefsManager.get();
      const dark = getThemeSettingsForMode(ThemeMode.Dark);
      prefsManager.patch({
        zeroDarkDefaultAppliedAt: new Date().toISOString(),
        ...(switchToDark
          ? { themeMode: dark.mode, themeColor: dark.color }
          : {}),
      });
      return { previous };
    },

    onSuccess: (data) => {
      prefsManager.replace(data);
    },

    onError: (_error, _variables, context) => {
      if (context?.previous) {
        prefsManager.replace(context.previous);
      }
    },
  });
};
